import React, { useEffect, useRef, useState } from "react";
import { initPlayer, initPlayerScript } from "./config";
import { Subscription } from "rxjs";
import { Slider } from "@arco-design/web-react";
import "./index.less";

import playIcon from "./icons/play.png";
import pauseIcon from "./icons/pause.png";
import fullscreenEnterIcon from "./icons/fullscreen-enter.png";
import fullscreenExitIcon from "./icons/fullscreen-exit.png";
interface VideoPreviewProps {
  dataSource?: any;
  myStyle?: React.CSSProperties;
  boxStyle?: React.CSSProperties;
}
const AIVideoPreview: React.FC<VideoPreviewProps> = (props) => {
  const { dataSource, myStyle = {}, boxStyle = {} } = props;

  // 参数
  const boxRef = useRef<any>(null); // 容器实例
  const containerRef = useRef<any>(null); // 播放器容器
  const playerRef = useRef<any>(null); // 播放器实例
  const [duration, setDuration] = useState<number>(0); // 视频时长
  const [currentTime, setCurrentTime] = useState<number>(0); // 当前播放时间
  const [isPlaying, setIsPlaying] = useState<boolean>(false); // 是否正在播放
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false); // 是否全屏显示
  const [isInit, setIsInit] = useState<boolean>(false); // 是否初始化完成

  // 方法
  const commEvent = {
    init: async () => {
      const res = await initPlayerScript();
      if (res === "ok") {
        const playRes: any = await initPlayer(containerRef.current, dataSource?.timeline);
        playerRef.current = playRes;
        playRes.controls = false;
        setIsInit(true);
      }
    },
    // 更新timeline配置
    changeTimeline: (data: any) => {
      const { timeline = {} } = data || {};
      if (playerRef?.current) {
        playerRef.current.setTimeline(timeline);
        playerRef.current.currentTime = 0;
      }
    },
    // 格式化时间
    formatTime: (time: number) => {
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes}:${seconds < 10 ? "0" + seconds : seconds}`;
    },
    // 获取进度比例
    getProgressRatio: (_duration: number) => {
      if (_duration < 100) {
        return _duration < 10 ? 100 : 10;
      }
      return 1;
    },
    // 进入/退出全屏
    onFullscreen: () => {
      if (!boxRef.current) return;
      if (isFullScreen) {
        const _document: any = document;
        if (_document.exitFullscreen) {
          _document.exitFullscreen();
        } else if (_document.webkitExitFullscreen) {
          _document.webkitExitFullscreen();
        } else if (_document.msExitFullscreen) {
          _document.msExitFullscreen();
        }
      } else {
        if (boxRef.current.requestFullscreen) {
          boxRef.current.requestFullscreen();
        } else if (boxRef.current.webkitRequestFullscreen) {
          boxRef.current.webkitRequestFullscreen();
        } else if (boxRef.current.msRequestFullscreen) {
          boxRef.current.msRequestFullscreen();
        }
        commEvent.handleResizeChange();
      }
    },
    // 全屏变化事件
    handleFullScreenChange: () => {
      const isFull = !!document.fullscreenElement;
      setIsFullScreen(isFull);
      if (!isFull) {
        containerRef.current.style.transform = `scale(1)`;
      }
    },
    // 窗口变化事件
    handleResizeChange: () => {
      if (!containerRef.current) return;
      const _winWidth = window.innerWidth;
      const _winHeight = window.innerHeight;
      const _myWidth: number = +myStyle?.width || 360;
      const _myHeight: number = +myStyle?.height || 640;
      let _ratio = _winWidth / _myWidth;
      if (_myWidth < _myHeight) {
        _ratio = _winHeight / _myHeight;
      }
      containerRef.current.style.transform = `scale(${_ratio})`;
    },
  };

  // 初始化
  useEffect(() => {
    commEvent.init();
    // 监听浏览器全屏事件
    document.addEventListener("fullscreenchange", commEvent.handleFullScreenChange);
    return () => {
      if (playerRef?.current) {
        playerRef.current.destroy();
        document.removeEventListener("fullscreenchange", commEvent.handleFullScreenChange);
      }
    };
  }, []);

  // 更新最新的timeline配置
  useEffect(() => {
    if (!dataSource || !isInit) return;
    commEvent.changeTimeline(dataSource);
    // 监听播放器事件
    if (!playerRef.current) return;
    const subscription = new Subscription();
    const eventSubscription = playerRef.current.event$.subscribe((event) => {
      switch (event.type) {
        case "render":
          setCurrentTime(playerRef.current.currentTime);
          setDuration(playerRef.current.duration);
          break;
        case "play":
          setIsPlaying(true);
          break;
        case "pause":
          setIsPlaying(false);
          break;
      }
    });
    subscription.add(eventSubscription);
    return () => {
      subscription.unsubscribe();
    };
  }, [dataSource, isInit]);

  return (
    <div className="ai-video-preview-box" style={{ ...boxStyle }} ref={boxRef}>
      <div ref={containerRef} style={{ width: 360, height: 640, ...myStyle }}></div>
      <div className="video_preview_controls">
        {/* 媒体控制 */}
        <div className="controls_operation">
          {isPlaying && duration !== currentTime ? (
            <img src={pauseIcon} className="operation_icon" onClick={() => playerRef.current.pause()} />
          ) : (
            <img src={playIcon} className="operation_icon" onClick={() => playerRef.current.play()} />
          )}
          <div className="operation_time">
            <div>{commEvent.formatTime(currentTime)}</div>/<div>{commEvent.formatTime(duration)}</div>
          </div>
          <img
            src={isFullScreen ? fullscreenExitIcon : fullscreenEnterIcon}
            className="operation_icon fullscreen_enter"
            onClick={() => commEvent.onFullscreen()}
          />
        </div>
        {/* 媒体进度 */}
        <div className="controls_progress">
          <Slider
            value={currentTime * commEvent.getProgressRatio(duration)}
            onChange={(value) => {
              const _ratio = commEvent.getProgressRatio(duration);
              const _time = +value / _ratio;
              setCurrentTime(_time);
              playerRef.current.currentTime = _time;
            }}
            formatTooltip={(value) => commEvent.formatTime(+value / commEvent.getProgressRatio(duration))}
            max={duration * commEvent.getProgressRatio(duration)}
          />
        </div>
      </div>
    </div>
  );
};
export default AIVideoPreview;
